import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  Icon,
  useColorModeValue,
  Badge,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export default function Plans() {
  const accentBorder = useColorModeValue('green.600', 'green.300');
  const subtleBorder = useColorModeValue('gray.200', 'gray.600');
  const subtleBg = useColorModeValue('white', 'gray.700');
  const highlightBg = useColorModeValue('green.50', 'green.900');

  const startCheckout = async (plan /* 'yearly' | 'monthly' */) => {
    const token = localStorage.getItem('token');
    if (!token) return alert('Please log in first.');

    try {
      const response = await fetch(`${API_BASE}/api/stripe/create-checkout-session/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan, // "yearly" or "monthly"
          success_url: `${window.location.origin}/dashboard`,
          cancel_url: `${window.location.origin}/plans`,
        }),
      });

      const data = await response.json();
      if (response.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        const msg =
          typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || data);
        alert(`Checkout failed: ${msg}`);
      }
    } catch (err) {
      console.error('❌ Checkout error:', err);
      alert('Something went wrong during checkout.');
    }
  };

  const features = [
    'AI Agents (CPG Cofounder, Buyer, Formulator, etc.)',
    'Pricing Tools',
    'Auto-Generated Charts & Exports',
  ];

  return (
    <Box p={10} bg="gray.50" minH="100vh">
      <Box textAlign="center" mb={10}>
        <Heading size="xl" mb={3}>Pick Your Pro Plan</Heading>
        <Text fontSize="lg" color="gray.600">
          Two simple options. Same features — choose how you want to pay.
        </Text>
      </Box>

      <Flex gap={8} justify="center" flexWrap="wrap">
        {/* Pro Monthly */}
        <Box
          border="2px solid"
          borderColor={subtleBorder}
          borderRadius="xl"
          boxShadow="sm"
          bg={subtleBg}
          p={8}
          maxW="sm"
          w="full"
          flex="1"
          minH="420px"
        >
          <Heading size="md" mb={1}>Pro Monthly</Heading>
          <Text fontSize="3xl" fontWeight="extrabold" color="green.600">$19.99</Text>
          <Text fontSize="sm" color="gray.600" mb={4}>per month</Text>

          <Button
            mb={5}
            colorScheme="green"
            width="100%"
            variant="outline"
            onClick={() => startCheckout('monthly')}
          >
            Start Monthly
          </Button>

          <Divider mb={5} />

          <VStack align="start" spacing={3}>
            {features.map((feature, i) => (
              <HStack key={i} spacing={2}>
                <Icon as={CheckCircleIcon} color="green.500" />
                <Text fontSize="sm">{feature}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        {/* Pro Yearly (Highlighted) */}
        <Box
          border="2px solid"
          borderColor={accentBorder}
          borderRadius="xl"
          boxShadow="md"
          bg={highlightBg}
          p={8}
          maxW="sm"
          w="full"
          flex="1"
          minH="420px"
          position="relative"
        >
          <HStack mb={1} align="center" justify="space-between">
            <Heading size="md">Pro Yearly</Heading>
            <Badge colorScheme="green">Best value</Badge>
          </HStack>

          <Text fontSize="3xl" fontWeight="extrabold" color="green.700">$189</Text>
          <Text fontSize="sm" color="green.700" mb={4}>per year • save ~21%</Text>

          <Button
            mb={5}
            colorScheme="green"
            width="100%"
            onClick={() => startCheckout('yearly')}
          >
            Start Yearly
          </Button>

          <Divider mb={5} borderColor="green.200" />

          <VStack align="start" spacing={3}>
            {features.map((feature, i) => (
              <HStack key={i} spacing={2}>
                <Icon as={CheckCircleIcon} color="green.600" />
                <Text fontSize="sm" color="green.900">{feature}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
}
